import { useState, useContext, memo } from 'react';
import type { Vector3 } from '@kitware/vtk.js/types';
import { encodeId } from '../../utils/representationId';
import { Handle } from './Handle';
import { AnnotationMutationsContext } from '../../modules/AnnotationMutationsContext';
import type { DreMouseEvent } from '../../types';
import type { SegmentPointPositionTuple, Segment } from '../types';

type HandlesProps = {
  visible: boolean;
  annotationId: string;
  segments: ReadonlyArray<Segment>;
  color: string;
  onMouseLeave: (arg1: DreMouseEvent) => unknown;
  onMouseOver: (arg1: DreMouseEvent) => unknown;
  mapper: (
    segmentIndex: number,
    pointIndex: number,
    mousePosition: Vector3
  ) => Array<SegmentPointPositionTuple>;
  opacity?: number;
  /** A filled square, or an empty circle, at each point. */
  shape?: 'square' | 'ring';
};

const DRAG_WARNING = `[SIRONA] Couldn't move handle. Either worldPosition or updateAnnotation is not defined.`;

export const Handles: React.ComponentType<HandlesProps> = memo(
  ({
    visible = true,
    opacity = 1,
    shape,
    annotationId,
    segments,
    color,
    onMouseLeave,
    onMouseOver,
    mapper,
  }: HandlesProps): React.ReactElement => {
    const [dragging, setDragging] = useState(false);
    const { updateAnnotationSegments } = useContext(AnnotationMutationsContext);

    // @ts-expect-error [EN-7967] - TS2739 - Type 'Element[]' is missing the following properties from type 'ReactElement<any, string | JSXElementConstructor<any>>': type, props, key
    return segments
      .map((segment, segmentIndex) =>
        segment.map((point, pointIndex) => {
          const name = `handle${segmentIndex}-${pointIndex}`;
          const serializedId = encodeId({
            id: annotationId,
            name,
          });
          return (
            <Handle
              key={serializedId}
              id={serializedId}
              data-testid={serializedId}
              point={point}
              color={color}
              opacity={dragging ? 1 : opacity}
              shape={shape}
              visible={visible}
              onMouseLeave={onMouseLeave}
              onMouseOver={onMouseOver}
              onDragStart={() => setDragging(true)}
              onDrag={({ worldPosition }) => {
                if (worldPosition == null || updateAnnotationSegments == null) {
                  console.warn(DRAG_WARNING);
                  return;
                }
                // Updates annotation locally
                updateAnnotationSegments(
                  annotationId,
                  true
                )(mapper(segmentIndex, pointIndex, worldPosition));
              }}
              onDragEnd={({ worldPosition }) => {
                setDragging(false);
                if (worldPosition == null || updateAnnotationSegments == null) {
                  console.warn(DRAG_WARNING);
                  return;
                }
                // Updates annotation on server
                updateAnnotationSegments(annotationId)(
                  mapper(segmentIndex, pointIndex, worldPosition)
                );
              }}
            />
          );
        })
      )
      .flat(1);
  }
);

Handles.displayName = 'Handles';
